import { Product, Decorator, ProductPointsBadgeProps, ProductPointsProps } from '@/types'
import {
  Badge,
  Badges,
  Header,
  Main,
  Pricing,
  ProductImage,
  TileContent,
  Title,
  Rating,
  Wrapper,
  PointsBadgeContainer,
  LinkWrapper,
  ProductTileWrap,
  HotDealTooltip,
  LargeTitle,
  Summary,
} from './ProductTile.styles'
import ProductPoints from './ProductPoints'
import ProductPointsBadge from './ProductPointsBadge'
import Icon, { IconName } from './Icons'
import { useContext } from 'react'
import { DefaultPropertiesContext } from '@/context/DefaultPropertiesContext'
import { CANONICAL_BASE_URL, getUrlPrefix } from '@/helpers/UrlHelper'
import { getDisplayHotDeal } from '@/helpers/BookingHelper'
import { formatCurrency } from '@big-red-group/storefront-common-checkout'
import { format } from 'date-fns'
import { ImgProxy, ReviewStars } from '@big-red-group/storefront-common-ui'
import { RichSnippetContext } from '@/context/RichSnippetContext'
import { DEFAULT_CURRENCY_CODE } from '@/constant/segment'

type ProductTileProps = Omit<Product, 'inclusions'> & {
  pointsProps?: ProductPointsProps
  pointsBadgeProps?: ProductPointsBadgeProps
  onClick: () => void
  smallMobileTile?: boolean
  dataMcpId?: string
  bookingEngineTile?: boolean
}

const now = format(new Date(), 'yyyy-MM-dd')

const ProductTile: React.FC<ProductTileProps> = ({
  id,
  url,
  badges,
  image,
  price,
  region,
  title,
  fromPrice,
  rating,
  pointsProps,
  pointsBadgeProps,
  hotDeals,
  onClick,
  smallMobileTile = false,
  dataMcpId,
  summary,
  bookingEngineTile = false,
  geolocations,
}) => {
  const defaultProperties = useContext(DefaultPropertiesContext)
  const baseUrl = getUrlPrefix(defaultProperties?.agentConfiguration?.handlerKey)
  const badgeNameIconMap: Record<string, IconName> = {
    BEST_SELLER: 'starIcon',
    MEALS_INCLUDED: 'bell',
  }

  const filterBadges = (badges?: Decorator[]) => {
    if (!badges) return []
    return badges.filter(({ name }) => name === 'BEST_SELLER' || name === 'MEALS_INCLUDED')
  }

  const renderBadges = () => {
    const filteredBadges = filterBadges(badges)
    return (
      <>
        {filteredBadges.map(({ name, label }) => (
          <Badge key={name} variant={name} data-testid={`badge-${name}`}>
            {badgeNameIconMap[name] && <Icon name={badgeNameIconMap[name]} />}
            {label}
          </Badge>
        ))}
      </>
    )
  }

  const renderTile = () => {
    if (bookingEngineTile) {
      return (
        <>
          <ProductImage className="product-tile__image">
            {hotDeals && (
              <Badges className="product-tile__badges">
                {hotDeals && (
                  <>
                    <Badge variant="HOT_DEAL">
                      <Icon name="flame" fill="white" width="12" height="12" />
                      Hot deal
                    </Badge>
                  </>
                )}
                {renderBadges()}
              </Badges>
            )}
            <ImgProxy alt={title} src={image} loading="lazy" width={600} height={360} />
            {hotDeals && <HotDealTooltip>{getDisplayHotDeal(hotDeals)}</HotDealTooltip>}
          </ProductImage>
          <Main data-testid="booking-engine-tile">
            <LargeTitle>{title}</LargeTitle>
            {summary && <Summary>{summary}</Summary>}
          </Main>
          <Pricing data-testid="product-tile-pricing" bookingEngineTile={bookingEngineTile} onSale={price != fromPrice}>
            from{' '}
            {fromPrice != price && price && (
              <>
                <s data-testid="product-tile-retail-price">{formatCurrency(price)}</s>
              </>
            )}{' '}
            {fromPrice && <span data-testid="product-tile-price">{formatCurrency(fromPrice)}</span>}
          </Pricing>
        </>
      )
    }

    return (
      <>
        <ProductImage data-mcp-id={`${dataMcpId}-tileImage`} className="product-tile__image">
          <ImgProxy alt={title} src={image} loading="lazy" width={600} height={360} />
          {hotDeals && (
            <HotDealTooltip className="product-tile__hot-deal">{getDisplayHotDeal(hotDeals)}</HotDealTooltip>
          )}
        </ProductImage>
        <TileContent data-mcp-id={`${dataMcpId}-tileContent`}>
          {region && <Header>{region}</Header>}
          <Main>
            <Title>{title}</Title>
            {/* TODO: Replace with something else like tour duration */}
            {/*  {supplier && <Supplier>by {supplier}</Supplier>} */}
          </Main>
          {rating && (
            /* TODO - Rating component */
            <Rating>
              {/* <Icon name="star" width={12} height={12} /> */}
              <ReviewStars rating={rating} starSize={16} />
              <span>{rating}</span>
            </Rating>
          )}
          {(hotDeals || filterBadges(badges).length > 0) && (
            <Badges className="product-tile__badges">
              {hotDeals && (
                <Badge variant="HOT_DEAL">
                  <Icon name="flame" fill="white" width="12" height="12" />
                  Hot deal
                </Badge>
              )}
              {renderBadges()}
            </Badges>
          )}
          {(fromPrice || price) && (
            <Pricing
              data-testid="product-tile-pricing"
              bookingEngineTile={bookingEngineTile}
              onSale={price != fromPrice}
            >
              from{' '}
              {fromPrice != price && price && <s data-testid="product-tile-retail-price">{formatCurrency(price)}</s>}{' '}
              {fromPrice && <span data-testid="product-tile-price">{formatCurrency(fromPrice!)}</span>}
              {/* TODO review, might be part of a decorations component */}
              {pointsProps?.points && (
                <ProductPoints
                  points={pointsProps.points}
                  description={pointsProps.description}
                  containerStyle={pointsProps.pointsStyles?.containerStyle}
                  separatorStyle={pointsProps.pointsStyles?.separatorStyle}
                  pointsStyle={pointsProps.pointsStyles?.pointsStyle}
                  descriptionStyle={pointsProps.pointsStyles?.descriptionStyle}
                />
              )}
              {pointsBadgeProps?.points && (
                <PointsBadgeContainer style={{ marginTop: '10px' }}>
                  <ProductPointsBadge
                    icon={pointsBadgeProps.icon}
                    points={pointsBadgeProps.points}
                    description={pointsBadgeProps.description}
                    style={pointsBadgeProps.style}
                  />
                </PointsBadgeContainer>
              )}
            </Pricing>
          )}
        </TileContent>
      </>
    )
  }

  const { collect } = RichSnippetContext.useRichSnippetStructuredData()

  collect({
    type: 'Carousel',
    data: [
      {
        url: `https://${CANONICAL_BASE_URL}${baseUrl}${url}`,
        name: title,
        image,
      },
    ],
  })

  return (
    <ProductTileWrap data-mcp-id={`${dataMcpId}-tile`} mobileTile={smallMobileTile}>
      <LinkWrapper href={`${baseUrl}${url}`} data-testid={`product-${id}`} onClick={onClick}>
        <Wrapper mobileTile={smallMobileTile}>{renderTile()}</Wrapper>
      </LinkWrapper>
    </ProductTileWrap>
  )
}

export default ProductTile
